import { AsyncButton } from "aic";
import { ChevronLeft, ChevronRight, Trash } from "react-bootstrap-icons";
import { LogicalOperator } from "./LogicalOperator";

/**
 * Props for the AnnotationNavigationButtons component.
 */
interface AnnotationNavigationButtonsProps {
  currentClauseIndex: number;
  totalClauseCount: number;
  canRemoveClause: boolean;
  canAddNewClause: boolean;
  disabled?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRemove: () => void;
  onAdd: (logicalOperator: LogicalOperator) => void;
}

/**
 * Buttons to navigate between where clauses and add/remove clauses.
 *
 * @param props - Destructured parameter object
 * @param props.currentClauseIndex - Index of the currently edited clause
 * @param props.totalClauseCount - Total number of clauses
 * @param props.canRemoveClause - Whether the current clause can be removed
 * @param props.canAddNewClause - Whether a new clause can be added
 * @param props.disabled - Optional disabled flag
 * @param props.onPrevious - Handler to move to previous clause
 * @param props.onNext - Handler to move to next clause
 * @param props.onRemove - Handler to remove current clause
 * @param props.onAdd - Handler to add a new clause with given logical operator
 */
export const AnnotationNavigationButtons = ({
  currentClauseIndex,
  totalClauseCount,
  canRemoveClause,
  canAddNewClause,
  disabled = false,
  onPrevious,
  onNext,
  onRemove,
  onAdd,
}: AnnotationNavigationButtonsProps): React.ReactElement => {
  return (
    <div>
      <AsyncButton
        id="annotation-prev-clause"
        variant="outline-secondary"
        className="me-2"
        onClick={onPrevious}
        disabled={disabled || currentClauseIndex === 0}
        size="sm"
        title="Previous clause"
      >
        <ChevronLeft size={16} />
      </AsyncButton>
      <AsyncButton
        id="annotation-next-clause"
        variant="outline-secondary"
        className="me-2"
        onClick={onNext}
        disabled={disabled || currentClauseIndex >= totalClauseCount - 1}
        size="sm"
        title="Next clause"
      >
        <ChevronRight size={16} />
      </AsyncButton>
      <AsyncButton
        id="annotation-remove-clause"
        variant="outline-danger"
        className="me-2"
        onClick={onRemove}
        disabled={disabled || !canRemoveClause}
        size="sm"
        title="Remove current clause"
      >
        <Trash size={16} />
      </AsyncButton>
      <AsyncButton
        id="annotation-add-clause"
        variant="primary"
        onClick={() => onAdd("AND")}
        disabled={disabled || !canAddNewClause}
        size="sm"
        title={canAddNewClause ? "Add new clause" : "Complete current clause first"}
      >
        AND
      </AsyncButton>
    </div>
  );
};

AnnotationNavigationButtons.displayName = "AnnotationNavigationButtons";
